import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import {
  Sparkles,
  Shield,
  School,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Sun,
  Moon,
  FileText,
  Calculator,
  Clock,
  Scale,
  TrendingUp,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  Timer,
  Zap,
  Upload,
  X,
  Smartphone,
  Share,
  PlusSquare,
  QrCode,
  Apple,
  Chrome,
  Monitor,
  ChevronLeft,
  MoreVertical,
  BookOpen,
  Loader2,
  RotateCw,
  Check,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import QRCode from 'qrcode'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { useTheme, applyTheme } from '../hooks/useTheme'
import { useIsMobile } from '../hooks/useIsMobile'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useUiLanguage } from '../hooks/useUiLanguage'
import type { Language } from '../i18n'
import { BrandMark } from '../components/BrandMark'
import { SAMPLE_RESULT } from '../landing/sampleResult'

/* ── Animated counter hook ── */
function useCountUp(end: number, duration = 2000) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref as React.RefObject<Element>, { once: true, margin: '-80px' })

  useEffect(() => {
    if (!isInView) return
    const startTime = Date.now()
    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(end * eased))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [isInView, end, duration])

  return { count, ref }
}

/* ── Fade-in section wrapper ── */
function FadeIn({
  children,
  delay = 0,
  className = '',
  y = 40,
}: {
  children: React.ReactNode
  delay?: number
  className?: string
  y?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/* ── Animated price display ── */
function AnimatedPrice({ value }: { value: string }) {
  return (
    <AnimatePresence mode="popLayout">
      <motion.span
        key={value}
        initial={{ opacity: 0, y: -20, filter: 'blur(4px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
        transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
        className="lp-calc-total"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  )
}

interface BenefitItem {
  icon: LucideIcon
  color: 'emerald' | 'purple' | 'blue' | 'amber'
  title: string
  description: string
}

interface FeatureItem {
  icon: LucideIcon
  color: 'emerald' | 'purple' | 'blue' | 'amber'
  title: string
  description: string
}

// Reading count helper
function calcReadings(totalStudents: number): { display: number; billing: number } {
  const rubricBuffer = 2
  return { display: totalStudents, billing: totalStudents + rubricBuffer }
}

// Pricing helper
function calcPricing(
  readings: number,
  examType: 'plain' | 'formula',
  pages: number,
): { pricePerCredit: number; discountPct: number; total: number } {
  const tiers = Math.floor(readings / 250)
  const discountPct = Math.min(tiers * 2.5, 50)
  const pageMultiplier = 1 + Math.max(0, pages - 3) * 0.1
  const examMult = examType === 'formula' ? 1.15 : 1.0
  const totalMult = pageMultiplier * examMult
  const basePricePerReading = 0.15
  const pricePerCredit = basePricePerReading * totalMult * (1 - discountPct / 100)
  return { pricePerCredit, discountPct, total: readings * pricePerCredit }
}

/** Landing TR price: convert USD→TL at this rate, then apply the 1/2.75 local factor. */
const USD_TRY_RATE = 50
const TR_PRICE_DIVISOR = 2.75

function formatLandingPrice(usd: number, language: Language, digits: number): string {
  if (language === 'tr') {
    const amount = (usd * USD_TRY_RATE) / TR_PRICE_DIVISOR
    return `${amount.toLocaleString('tr-TR', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })} TL`
  }
  return `$${usd.toFixed(digits)}`
}

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, Math.round(n)))
}

function EditableStepValue({
  value,
  onCommit,
  min,
  max,
  format,
}: {
  value: number
  onCommit: (n: number) => void
  min: number
  max: number
  format?: (n: number) => string
}) {
  const display = format ? format(value) : String(value)
  const [draft, setDraft] = useState(display)

  useEffect(() => {
    setDraft(format ? format(value) : String(value))
  }, [value, format])

  const commit = (raw: string) => {
    const cleaned = raw.replace(/\D/g, '')
    const n = cleaned === '' ? min : clampInt(parseInt(cleaned, 10), min, max)
    onCommit(n)
    setDraft(format ? format(n) : String(n))
  }

  return (
    <input
      className="lp-calc-step-val lp-calc-step-input"
      type="text"
      inputMode="numeric"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      onFocus={(e) => e.target.select()}
    />
  )
}

const stats = [
  { end: 50000, label: 'Değerlendirilen Sınav', prefix: '', suffix: '+' },
  { end: 2400, label: 'Eğitimci', prefix: '', suffix: '+' },
  { end: 98, label: 'Doğruluk Oranı', prefix: '%', suffix: '' },
  { end: 85, label: 'Zaman Tasarrufu', prefix: '%', suffix: '' },
]

/* ── Stat item with count-up ── */
function StatItem({ end, prefix, suffix, label }: {
  end: number; prefix: string; suffix: string; label: string
}) {
  const { count, ref } = useCountUp(end, 2200)

  const formatted = end >= 10000
    ? `${Math.floor(count / 1000)}K`
    : count >= 1000
      ? count.toLocaleString('tr-TR')
      : String(count)

  return (
    <div className="lp-stat">
      <div className="lp-stat-value" ref={ref}>
        {prefix}{formatted}{suffix}
      </div>
      <div className="lp-stat-label">{label}</div>
    </div>
  )
}

/* ── Benefit card with animated stat ── */
function BenefitCard({
  benefit,
  index,
}: {
  benefit: BenefitItem
  index: number
}) {
  const Icon = benefit.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.15, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={{ y: -4, transition: { duration: 0.25 } }}
      className="lp-glass-card lp-benefit-card"
    >
      <div className={`lp-benefit-icon ${benefit.color}`}>
        <Icon size={28} />
      </div>

      <h3>{benefit.title}</h3>
      <p>{benefit.description}</p>
    </motion.div>
  )
}

function FaqItem({
  feature,
  index,
}: {
  feature: FeatureItem
  index: number
}) {
  const [open, setOpen] = useState(false)
  const Icon = feature.icon

  return (
    <motion.div
      className={`lp-faq-item${open ? ' open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <button className="lp-faq-question" onClick={() => setOpen(o => !o)}>
        <div className="lp-faq-question-left">
          <div className={`lp-faq-icon ${feature.color}`}>
            <Icon size={18} />
          </div>
          <span>{feature.title}</span>
        </div>
        <ChevronDown size={18} className={`lp-faq-chevron${open ? ' open' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="lp-faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <p>{feature.description}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/* ── Mobile PWA install promo ── */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Only set by a REAL install signal (Android `appinstalled`). Versioned so any
// older, prematurely-written flag is ignored.
const INSTALLED_KEY = 'ai-grader-installed-v2'

function useInstalled() {
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    // Reliable signal: page is running in standalone (launched from home screen).
    const mq = window.matchMedia?.('(display-mode: standalone)')
    const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true
    // Persisted only after a genuine install (appinstalled event).
    const confirmed = localStorage.getItem(INSTALLED_KEY) === '1'
    setInstalled(!!mq?.matches || iosStandalone || confirmed)
    const onChange = (e: MediaQueryListEvent) => setInstalled(e.matches || confirmed)
    mq?.addEventListener?.('change', onChange)
    return () => mq?.removeEventListener?.('change', onChange)
  }, [])

  const markInstalled = useCallback(() => {
    try { localStorage.setItem(INSTALLED_KEY, '1') } catch { /* ignore */ }
    setInstalled(true)
  }, [])

  return { installed, markInstalled }
}

function MobileAppSection() {
  const navigate = useNavigate()
  const { t } = useTranslation('landing')
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIOS = /iphone|ipad|ipod/i.test(ua)
  const isAndroid = /android/i.test(ua)
  const isMobile = isIOS || isAndroid

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const { installed, markInstalled } = useInstalled()
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')

  // Capture the deferred install prompt (Android/Chromium only).
  useEffect(() => {
    if (!isMobile) return
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      markInstalled()
      setDeferredPrompt(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [isMobile, markInstalled])

  // Desktop: generate a QR pointing at the mobile app URL of THIS origin
  // (works correctly through the cloudflared tunnel too).
  useEffect(() => {
    if (isMobile) return
    const url = `${window.location.origin}/m`
    QRCode.toDataURL(url, { width: 240, margin: 1, color: { dark: '#0f172a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''))
  }, [isMobile])

  const triggerInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') {
        markInstalled()
        setShowOnboarding(false)
      }
      setDeferredPrompt(null)
    }
  }

  const handlePrimary = () => {
    if (installed) {
      navigate('/m')
      return
    }
    // Android with a real prompt available → fire it directly.
    if (isAndroid && deferredPrompt) {
      triggerInstall()
      return
    }
    // iOS or Android without prompt → onboarding modal.
    setShowOnboarding(true)
  }

  const primaryLabel = installed
    ? t('mobile.openApp')
    : isMobile
      ? t('mobile.installMobile')
      : t('mobile.openOnPhone')

  return (
    <section className="lp-mobile" id="mobile">
      <FadeIn className="lp-glass-card lp-mobile-card">
        <div className="lp-mobile-left">
          <span className="lp-showcase-eyebrow">
            <Smartphone size={14} /> {t('mobile.eyebrow')}
          </span>
          <h2>
            {t('mobile.titleLead')} <span className="lp-gradient-text">{t('mobile.titleEmph')}</span>
          </h2>
          <p>
            {t('mobile.description')}
          </p>

          {/* ── Desktop: QR code ── */}
          {!isMobile && (
            <div className="lp-mobile-qr">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt={t('mobile.qrAlt')} width={200} height={200} />
              ) : (
                <div className="lp-mobile-qr-placeholder" />
              )}
              <div className="lp-mobile-qr-cap">
                <QrCode size={15} />
                <span>{t('mobile.qrCaption')}</span>
              </div>
            </div>
          )}

          {/* ── Mobile: install actions ── */}
          {isMobile && (
            <div className="lp-mobile-actions">
              <button className="lp-btn-primary lp-btn-lg" onClick={handlePrimary}>
                {installed ? <Smartphone size={18} /> : <PlusSquare size={18} />} {primaryLabel}
              </button>
              {!installed && (
                <button className="lp-onboard-link" onClick={() => navigate('/m')}>
                  {t('mobile.tryFirst')}
                </button>
              )}
              {installed && (
                <button className="lp-onboard-link" onClick={() => setShowOnboarding(true)}>
                  {t('mobile.reinstall')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="lp-mobile-phone" aria-hidden="true">
          <div className="lp-mobile-phone-frame">
            <div className="lp-mobile-phone-notch" />
            <div className="lp-mobile-phone-screen">
              <div className="lp-mobile-phone-top">
                <BrandMark size={18} alt="" />
                <span>{t('appName', { ns: 'common' })}</span>
              </div>
              <div className="lp-mobile-phone-card">
                <div className="lp-mobile-phone-ring">84</div>
                <div className="lp-mobile-phone-lines">
                  <span style={{ width: '70%' }} />
                  <span style={{ width: '45%' }} />
                </div>
              </div>
              <div className="lp-mobile-phone-card lp-mobile-phone-card--alt">
                <Sparkles size={14} />
                <div className="lp-mobile-phone-lines">
                  <span style={{ width: '80%' }} />
                  <span style={{ width: '55%' }} />
                </div>
              </div>
              <div className="lp-mobile-phone-scan">
                <PlusSquare size={18} /> {t('mobile.scanPaper')}
              </div>
            </div>
          </div>
        </div>
      </FadeIn>

      {showOnboarding && (
        <InstallOnboarding
          isIOS={isIOS}
          isAndroid={isAndroid}
          hasPrompt={!!deferredPrompt}
          onClose={() => setShowOnboarding(false)}
          onInstall={triggerInstall}
          onOpenApp={() => {
            setShowOnboarding(false)
            navigate('/m')
          }}
        />
      )}
    </section>
  )
}

/* ── Install onboarding modal (mobile only) ── */
function InstallOnboarding({
  isIOS,
  isAndroid,
  hasPrompt,
  onClose,
  onInstall,
  onOpenApp,
}: {
  isIOS: boolean
  isAndroid: boolean
  hasPrompt: boolean
  onClose: () => void
  onInstall: () => void
  onOpenApp: () => void
}) {
  const { t } = useTranslation('landing')
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  // iOS instructions are browser-agnostic: both Safari and Chrome on iOS
  // expose "Add to Home Screen" through the Share/Menü button.
  const iosSteps = [
    { kind: 'ios-share' as const, text: t('mobile.onboarding.iosShare') },
    { kind: 'add-row' as const, text: t('mobile.onboarding.iosAdd') },
    { kind: 'home' as const, text: t('mobile.onboarding.iosHome') },
  ]
  const androidSteps = [
    { kind: 'android-menu' as const, text: t('mobile.onboarding.androidMenu') },
    { kind: 'install-row' as const, text: t('mobile.onboarding.androidInstall') },
    { kind: 'home' as const, text: t('mobile.onboarding.androidHome') },
  ]
  const steps = isIOS ? iosSteps : androidSteps
  const PlatformIcon = isIOS ? Apple : Chrome

  return createPortal(
    <div className="lp-onboard-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="lp-onboard-card" onClick={(e) => e.stopPropagation()}>
        <button className="lp-onboard-close" onClick={onClose} aria-label={t('mobile.onboarding.close')}>
          <X size={18} />
        </button>

        <div className="lp-onboard-head">
          <span className="lp-onboard-platform">
            <PlatformIcon size={18} /> {isIOS ? t('mobile.onboarding.iosPlatform') : t('mobile.onboarding.androidPlatform')}
          </span>
          <h3>{t('mobile.onboarding.title')}</h3>
          <p>{t('mobile.onboarding.takes')}</p>
        </div>

        <ol className="lp-onboard-steps">
          {steps.map((s, i) => (
            <li key={i} className="lp-onboard-vstep">
              <div className="lp-onboard-vstep-head">
                <span className="lp-onboard-step-num">{i + 1}</span>
                <span className="lp-onboard-vstep-text">{s.text}</span>
              </div>
              <OnboardVisual kind={s.kind} />
            </li>
          ))}
        </ol>

        <div className="lp-onboard-actions">
          {isAndroid && hasPrompt && (
            <button className="lp-btn-primary lp-btn-lg" onClick={onInstall}>
              <PlusSquare size={18} /> {t('mobile.onboarding.installNow')}
            </button>
          )}
          <div className="lp-onboard-or"><span>{t('mobile.onboarding.or')}</span></div>
          <button className="lp-onboard-link" onClick={onOpenApp}>
            {t('mobile.onboarding.tryAppFirst')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ── Illustrated step visuals (CSS-only device mockups) ── */
type OnboardVisualKind = 'ios-share' | 'add-row' | 'android-menu' | 'install-row' | 'home'

function OnboardVisual({ kind }: { kind: OnboardVisualKind }) {
  const { t } = useTranslation('landing')
  if (kind === 'ios-share') {
    return (
      <div className="ob-viz">
        <div className="ob-toolbar">
          <ChevronLeft size={17} className="ob-dim" />
          <ChevronRight size={17} className="ob-dim" />
          <span className="ob-key ob-key--pulse"><Share size={18} /></span>
          <BookOpen size={17} className="ob-dim" />
          <Smartphone size={17} className="ob-dim" />
        </div>
      </div>
    )
  }
  if (kind === 'android-menu') {
    return (
      <div className="ob-viz">
        <div className="ob-toolbar ob-toolbar--top">
          <span className="ob-urlpill" />
          <span className="ob-key ob-key--pulse"><MoreVertical size={18} /></span>
        </div>
        <span className="ob-caption">{t('mobile.onboarding.chromeTopBar')}</span>
      </div>
    )
  }
  if (kind === 'add-row' || kind === 'install-row') {
    const label = kind === 'add-row' ? t('mobile.onboarding.addToHome') : t('mobile.onboarding.installApp')
    return (
      <div className="ob-viz">
        <div className="ob-menu">
          <div className="ob-menurow ob-menurow--blank"><span /></div>
          <div className="ob-menurow ob-menurow--active">
            <span>{label}</span>
            <PlusSquare size={17} />
          </div>
        </div>
      </div>
    )
  }
  // home
  return (
    <div className="ob-viz">
      <div className="ob-homerow">
        <span className="ob-appdot" />
        <span className="ob-appicon ob-key--pulse"><BrandMark size={38} alt="" /></span>
        <span className="ob-appdot" />
        <span className="ob-appdot" />
      </div>
    </div>
  )
}

export default function Landing() {
  const navigate = useNavigate()
  const { t } = useTranslation('landing')
  const { language } = useUiLanguage()
  const benefits = useMemo<BenefitItem[]>(() => [
    {
      icon: Clock,
      color: 'emerald',
      title: t('benefits.time.title'),
      description: t('benefits.time.description'),
    },
    {
      icon: Scale,
      color: 'emerald',
      title: t('benefits.fair.title'),
      description: t('benefits.fair.description'),
    },
    {
      icon: BarChart3,
      color: 'emerald',
      title: t('benefits.insights.title'),
      description: t('benefits.insights.description'),
    },
  ], [t])
  const compactFeatures = useMemo<FeatureItem[]>(() => {
    const features: FeatureItem[] = [
      {
        icon: Shield,
        color: 'emerald',
        title: t('features.finalCall.title'),
        description: t('features.finalCall.description'),
      },
      {
        icon: CheckCircle,
        color: 'purple',
        title: t('features.rubric.title'),
        description: t('features.rubric.description'),
      },
      {
        icon: MessageSquare,
        color: 'blue',
        title: t('features.perQuestion.title'),
        description: t('features.perQuestion.description'),
      },
      {
        icon: FileText,
        color: 'purple',
        title: t('features.reports.title'),
        description: t('features.reports.description'),
      },
      {
        icon: TrendingUp,
        color: 'emerald',
        title: t('features.progress.title'),
        description: t('features.progress.description'),
      },
    ]
    // e-Okul is a Türkiye-specific system, so only surface it on the Turkish page.
    if (language !== 'tr') return features
    return [
      ...features.slice(0, 4),
      {
        icon: School,
        color: 'amber',
        title: t('features.eokul.title'),
        description: t('features.eokul.description'),
      },
      ...features.slice(4),
    ]
  }, [t, language])
  const { theme, toggleTheme } = useTheme()
  const isMobile = useIsMobile()
  const [examType, setExamType] = useState<'plain' | 'formula'>('plain')
  const [pagesPerStudent, setPagesPerStudent] = useState(3)
  const [studentsPerClass, setStudentsPerClass] = useState(25)
  const [examsPerYear, setExamsPerYear] = useState(4)
  const [annualPapers, setAnnualPapers] = useState(100)
  const [studentFile, setStudentFile] = useState<File | null>(null)
  const [rubricFile, setRubricFile] = useState<File | null>(null)
  const studentFileRef = useRef<HTMLInputElement>(null)
  const rubricFileRef = useRef<HTMLInputElement>(null)
  // Preloaded sample files — held in refs so they are ready instantly when
  // the visitor picks them from the fake file picker, without auto-filling
  // the upload widget on page load.
  const preloadedPaperPages = useRef<File[]>([])
  const preloadedPaperUrls = useRef<string[]>([])
  const preloadedRubric = useRef<File | null>(null)
  const preloadedRubricUrl = useRef<string>('')
  // Fake file picker open state: null = closed, 'student' | 'rubric' = open
  const [fakePicker, setFakePicker] = useState<'student' | 'rubric' | null>(null)
  // Quick-assess demo state: 'idle' shows the decorative showcase, 'grading'
  // plays the animation, 'done' renders the pre-cooked SAMPLE_RESULT report.
  const [gradingState, setGradingState] = useState<'idle' | 'grading' | 'done'>('idle')
  const [gradingStep, setGradingStep] = useState(0)
  // Object URLs for the two preset pages — used to render real image previews
  // inside the showcase paper frame. Index 0 = s1.jpeg, 1 = s1-2.jpeg.
  const [studentImageUrls, setStudentImageUrls] = useState<string[]>([])
  const [studentImageUrl, setStudentImageUrl] = useState<string>('')
  const [previewPage, setPreviewPage] = useState(0)
  const gradingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [showcaseSubject, setShowcaseSubject] = useState<'bio' | 'math'>('bio')
  const showcaseRef = useRef<HTMLElement>(null)
  const [showcaseVisible, setShowcaseVisible] = useState(true)

  // On mobile, force the light theme regardless of stored preference.
  // The stored preference is preserved (state is not changed), so returning
  // to desktop restores the user's chosen theme.
  useEffect(() => {
    if (isMobile) applyTheme('light')
    else applyTheme(theme)
  }, [isMobile, theme])

  // Pause the auto paper-switch when the showcase scrolls out of the viewport,
  // so it doesn't yank the page while the user reads sections below.
  useEffect(() => {
    const el = showcaseRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setShowcaseVisible(entry.isIntersecting),
      { threshold: 0.25 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!showcaseVisible) return
    if (gradingState !== 'idle') return
    const durations: Record<'bio' | 'math', number> = { bio: 3000, math: 4000 }
    let timeout: ReturnType<typeof setTimeout>
    const schedule = (current: 'bio' | 'math') => {
      timeout = setTimeout(() => {
        const next: 'bio' | 'math' = current === 'bio' ? 'math' : 'bio'
        setShowcaseSubject(next)
        schedule(next)
      }, durations[current])
    }
    schedule(showcaseSubject)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showcaseVisible, gradingState])

  // Preload the demo preset on first mount so the fake file picker can hand
  // files to the state instantly. Also builds object URLs for both preset pages
  // used by the image preview carousel.
  useEffect(() => {
    let cancelled = false
    const loadFileFromUrl = async (url: string, name: string, mime: string) => {
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error(`status ${res.status}`)
        const blob = await res.blob()
        if (cancelled) return null
        return new File([blob], name, { type: mime || blob.type || 'application/octet-stream' })
      } catch {
        return null
      }
    }
    ;(async () => {
      const [paper, paper2, rubric] = await Promise.all([
        loadFileFromUrl('/samples/student-paper.jpeg', 'sayfa1.jpeg', 'image/jpeg'),
        loadFileFromUrl('/samples/student-paper-2.jpeg', 'sayfa2.jpeg', 'image/jpeg'),
        loadFileFromUrl('/samples/rubric.pdf', 'rubric.pdf', 'application/pdf'),
      ])
      if (cancelled) return
      // Store in refs — do NOT auto-fill the upload widget.
      const pages: File[] = []
      if (paper) pages.push(paper)
      if (paper2) pages.push(paper2)
      preloadedPaperPages.current = pages
      if (rubric) preloadedRubric.current = rubric
      // Build object URLs for both pages so the carousel works offline too.
      const urls: string[] = []
      if (paper) urls.push(URL.createObjectURL(paper))
      if (paper2) urls.push(URL.createObjectURL(paper2))
      preloadedPaperUrls.current = urls
      if (urls.length) {
        setStudentImageUrls(urls)
        setStudentImageUrl(urls[0])
      }
      if (rubric) preloadedRubricUrl.current = URL.createObjectURL(rubric)
    })()
    return () => { cancelled = true }
  }, [])

  // When the displayed page index changes, update the single URL used by the
  // paper frame so the existing image tag just re-sources.
  useEffect(() => {
    if (studentImageUrls.length === 0) return
    setStudentImageUrl(studentImageUrls[previewPage] ?? studentImageUrls[0])
  }, [previewPage, studentImageUrls])

  // Drive the fake grading progress. Each tick advances one "question" so
  // the visitor sees the loader step through all 9 items in SAMPLE_RESULT
  // before the report unlocks.
  useEffect(() => {
    if (gradingState !== 'grading') return
    setGradingStep(0)
    const totalSteps = SAMPLE_RESULT.questions.length
    const stepMs = 380
    gradingTimerRef.current = setInterval(() => {
      setGradingStep(prev => {
        const next = prev + 1
        if (next >= totalSteps) {
          if (gradingTimerRef.current) clearInterval(gradingTimerRef.current)
          gradingTimerRef.current = null
          setGradingState('done')
          return totalSteps
        }
        return next
      })
    }, stepMs)
    return () => {
      if (gradingTimerRef.current) {
        clearInterval(gradingTimerRef.current)
        gradingTimerRef.current = null
      }
    }
  }, [gradingState])

  // When grading finishes scroll the showcase into view so the visitor
  // actually sees the result without having to scroll manually.
  useEffect(() => {
    if (gradingState !== 'done') return
    const el = showcaseRef.current
    if (!el) return
    // Small delay so the 'done' render has time to paint before scrolling.
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => clearTimeout(t)
  }, [gradingState])

  const handleStartQuickAssess = useCallback(() => {
    if (gradingState === 'idle') {
      setGradingState('grading')
      setTimeout(() => {
        showcaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    } else if (gradingState === 'done') {
      setGradingStep(0)
      setPreviewPage(0)
      setGradingState('idle')
    }
  }, [gradingState])

  const { display: displayReadingsPerExam, billing: billingReadingsPerExam } = calcReadings(studentsPerClass)
  const displayAnnualReadings = isMobile ? annualPapers : displayReadingsPerExam * examsPerYear
  const billingAnnualReadings = isMobile ? annualPapers : billingReadingsPerExam * examsPerYear
  const annualPricingRaw = calcPricing(billingAnnualReadings, examType, isMobile ? 3 : pagesPerStudent)
  const annualPricing = { ...annualPricingRaw, total: annualPricingRaw.total * 3 }
  const displayTotal = formatLandingPrice(annualPricing.total, language, 2)
  const displayPerReading = formatLandingPrice(annualPricing.pricePerCredit, language, 4)
  const readingsLocale = language === 'tr' ? 'tr-TR' : language === 'nl' ? 'nl-NL' : 'en-US'

  return (
    <div className="lp">
      <div className="lp-gradient-mesh">
        <div className="lp-orb lp-orb-1" />
        <div className="lp-orb lp-orb-2" />
        <div className="lp-orb lp-orb-3" />
      </div>

      {/* Nav */}
      <nav className="lp-nav">
        <div className="lp-logo">
          <BrandMark size={40} className="lp-logo-mark" alt="" />
          <span className="lp-logo-text">{t('appName', { ns: 'common' })}</span>
        </div>
        <div className="lp-nav-links">
          <LanguageSwitcher variant="compact" />
          <a href="#features" className="lp-nav-link">{t('nav.features')}</a>
          <a href="#pricing" className="lp-nav-link">{t('nav.pricing')}</a>
          {!isMobile && (
            <button
              className="lp-theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? t('nav.toLight') : t('nav.toDark')}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}
          <button className="lp-btn-ghost lp-btn-sm" onClick={() => navigate('/demo')}>
            {t('nav.demo')}
          </button>
          <button className="lp-btn-ghost lp-btn-sm" onClick={() => navigate('/exams')}>
            {t('nav.login')}
          </button>
          {!isMobile && (
            <button className="lp-btn-primary lp-btn-sm" onClick={() => navigate('/exams')}>
              {t('nav.getStarted')} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="lp-hero">
        {/* Left: text content */}
        <div className="lp-hero-left">
          <motion.div
            className="lp-hero-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles size={14} />
            {t('hero.badge')}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {t('hero.titleLead')} <span className="lp-gradient-text">{t('hero.titleEmph')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            className="lp-hero-trusted"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <span className="lp-trusted-label">{t('hero.trustedLabel')}</span>
            <div className="lp-trusted-badges">
              {['Stanford', 'MIT', 'Harvard', 'Oxford', 'Berkeley'].map(school => (
                <span key={school} className="lp-school-badge">{school}</span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right: upload widget */}
        <motion.div
          className="lp-hero-right"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="lp-upload-widget">
            <div className="lp-upload-widget-header">
              <span className="lp-upload-widget-eyebrow">{t('upload.eyebrow')}</span>
            </div>
            <div className="lp-upload-field">
              <label className="lp-upload-label">{t('upload.studentLabel')}</label>
              <div
                className={`lp-upload-zone${studentFile ? ' lp-upload-zone--filled' : ''}`}
                onClick={() => !studentFile && setFakePicker('student')}
              >
                {studentFile ? (
                  <div className="lp-upload-file-row">
                    <FileText size={16} />
                    <span className="lp-upload-filename">{studentFile.name}</span>
                    <button
                      className="lp-upload-remove"
                      onClick={e => { e.stopPropagation(); setStudentFile(null) }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="lp-upload-placeholder">
                    <div className="lp-upload-icon-wrap">
                      <Upload size={20} />
                    </div>
                    <span className="lp-upload-cta">
                      <Trans i18nKey="upload.dragOrPick" t={t} components={{ 1: <span className="lp-upload-link" /> }} />
                    </span>
                    <span className="lp-upload-types">{t('upload.studentTypes')}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.tiff"
                  style={{ display: 'none' }}
                  ref={studentFileRef}
                  onChange={e => setStudentFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>

            {/* Rubric upload */}
            <div className="lp-upload-field">
              <label className="lp-upload-label">{t('upload.rubricLabel')}</label>
              <div
                className={`lp-upload-zone${rubricFile ? ' lp-upload-zone--filled' : ''}`}
                onClick={() => !rubricFile && setFakePicker('rubric')}
              >
                {rubricFile ? (
                  <div className="lp-upload-file-row">
                    <FileText size={16} />
                    <span className="lp-upload-filename">{rubricFile.name}</span>
                    <button
                      className="lp-upload-remove"
                      onClick={e => { e.stopPropagation(); setRubricFile(null) }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="lp-upload-placeholder">
                    <div className="lp-upload-icon-wrap">
                      <Upload size={20} />
                    </div>
                    <span className="lp-upload-cta">
                      <Trans i18nKey="upload.dragOrPick" t={t} components={{ 1: <span className="lp-upload-link" /> }} />
                    </span>
                    <span className="lp-upload-types">{t('upload.rubricTypes')}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  ref={rubricFileRef}
                  onChange={e => setRubricFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>

            <button
              className="lp-btn-primary lp-btn-full"
              disabled={!studentFile || !rubricFile || gradingState === 'grading'}
              onClick={handleStartQuickAssess}
            >
              {gradingState === 'grading' ? (
                <>
                  <Loader2 size={16} className="lp-spin" />
                  {t('upload.grading')}
                </>
              ) : gradingState === 'done' ? (
                <>
                  <RotateCw size={16} />
                  {t('upload.watchAgain')}
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  {t('upload.startGrading')}
                </>
              )}
            </button>

            {/* Mini result summary — visible immediately after grading completes,
                so the user sees the score without having to scroll down. */}
            <AnimatePresence>
              {gradingState === 'done' && (
                <motion.div
                  className="lp-widget-result-bar"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <CheckCircle size={16} className="lp-widget-result-icon" />
                  <span className="lp-widget-result-score">
                    <strong>{SAMPLE_RESULT.totalScore}/{SAMPLE_RESULT.maxPoints}</strong>
                  </span>
                  <span className="lp-widget-result-label">
                    {t('upload.resultDone')}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Fake file picker modal */}
        <AnimatePresence>
          {fakePicker && (
            <motion.div
              className="lp-fake-picker-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setFakePicker(null)}
            >
              <motion.div
                className="lp-fake-picker"
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ duration: 0.18 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="lp-fake-picker-header">
                  <span className="lp-fake-picker-title">
                    {fakePicker === 'student' ? t('upload.pickerStudentTitle') : t('upload.pickerRubricTitle')}
                  </span>
                  <button className="lp-fake-picker-close" onClick={() => setFakePicker(null)}>
                    <X size={16} />
                  </button>
                </div>

                {fakePicker === 'student' ? (
                  <>
                    <div className="lp-fake-picker-previews">
                      {preloadedPaperUrls.current.map((url, i) => (
                        <div key={i} className="lp-fake-picker-preview-item lp-fake-picker-preview-item--checked">
                          <div className="lp-fake-picker-preview-img-wrap">
                            <img src={url} alt={t('upload.page', { n: i + 1 })} className="lp-fake-picker-preview-img" />
                            <div className="lp-fake-picker-check">
                              <Check size={12} />
                            </div>
                          </div>
                          <span className="lp-fake-picker-preview-label">{t('upload.page', { n: i + 1 })}</span>
                        </div>
                      ))}
                    </div>
                    <div className="lp-fake-picker-footer">
                      <button
                        className="lp-fake-picker-confirm"
                        onClick={() => {
                          const pages = preloadedPaperPages.current
                          if (pages.length > 0) setStudentFile(pages[0])
                          setFakePicker(null)
                        }}
                      >
                        {t('upload.filesSelected', { count: preloadedPaperUrls.current.length })}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="lp-fake-picker-previews lp-fake-picker-previews--rubric">
                      <div className="lp-fake-picker-preview-item lp-fake-picker-preview-item--checked">
                        <div className="lp-fake-picker-preview-img-wrap">
                          <img src="/samples/rubric-preview.png" alt="rubric.pdf" className="lp-fake-picker-preview-img" />
                          <div className="lp-fake-picker-check">
                            <Check size={12} />
                          </div>
                        </div>
                        <span className="lp-fake-picker-preview-label">rubric.pdf</span>
                      </div>
                    </div>
                    <div className="lp-fake-picker-footer">
                      <button
                        className="lp-fake-picker-confirm"
                        onClick={() => {
                          if (preloadedRubric.current) setRubricFile(preloadedRubric.current)
                          setFakePicker(null)
                        }}
                      >
                        {t('upload.oneFileSelected')}
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Showcase */}
      <section className="lp-showcase" ref={showcaseRef}>
        <FadeIn className="lp-showcase-header">
          <span className="lp-showcase-eyebrow">{t('showcase.eyebrow')}</span>
          <h2>
            {t('showcase.titleLead')}{' '}
            <span className="lp-gradient-text">{t('showcase.titleEmph')}</span>
          </h2>
          <p>{t('showcase.subtitle')}</p>
        </FadeIn>

        <div className="lp-showcase-grid">
          {/* Left: exam paper preview — bio/math mockup (idle) or the real
              uploaded/preloaded student paper image (grading & done). */}
          <FadeIn delay={0.1} className="lp-showcase-col">
            <div className="lp-showcase-label">
              <span className="lp-showcase-dot lp-dot-paper" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={gradingState === 'idle' ? showcaseSubject + '-label' : 'sample-label'}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {gradingState === 'idle'
                    ? (showcaseSubject === 'bio' ? t('showcase.bioExam') : t('showcase.mathExam'))
                    : t('showcase.sampleMathPaper')}
                </motion.span>
              </AnimatePresence>
            </div>

            <div className="lp-paper-frame">
              <AnimatePresence mode="wait">
                {gradingState !== 'idle' ? (
                  <motion.div
                    key="sample-sheet"
                    className="lp-exam-sheet lp-exam-sheet--image"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    {studentImageUrl ? (
                      <>
                        <AnimatePresence mode="wait">
                          <motion.img
                            key={previewPage}
                            src={studentImageUrl}
                            alt={t('showcase.studentPaperPage', { n: previewPage + 1 })}
                            className="lp-sample-paper-img"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                          />
                        </AnimatePresence>
                        {/* Page navigation — only shown when there are multiple pages */}
                        {studentImageUrls.length > 1 && (
                          <div className="lp-paper-page-nav">
                            <button
                              className="lp-paper-page-btn"
                              disabled={previewPage === 0}
                              onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                              aria-label={t('showcase.prevPage')}
                            >
                              <ChevronLeft size={16} />
                            </button>
                            <span className="lp-paper-page-label">
                              {t('showcase.pageNav', { current: previewPage + 1, total: studentImageUrls.length })}
                            </span>
                            <button
                              className="lp-paper-page-btn"
                              disabled={previewPage >= studentImageUrls.length - 1}
                              onClick={() => setPreviewPage(p => Math.min(studentImageUrls.length - 1, p + 1))}
                              aria-label={t('showcase.nextPage')}
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="lp-sample-paper-placeholder">{t('showcase.previewLoading')}</div>
                    )}
                  </motion.div>
                ) : showcaseSubject === 'bio' ? (
                  <motion.div
                    key="bio-sheet"
                    className="lp-exam-sheet"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="lp-exam-school-header">
                      <div className="lp-exam-school-name">{t('showcase.examInfo.schoolName')}</div>
                      <div className="lp-exam-title-row">
                        <span className="lp-exam-course">{t('showcase.examInfo.bioCourse')}</span>
                        <span className="lp-exam-term">{t('showcase.examInfo.term')}</span>
                      </div>
                      <div className="lp-paper-badge lp-paper-badge--inline">{t('showcase.uploaded')}</div>
                    </div>
                    <div className="lp-exam-info-grid">
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.name')}</span>
                        <span className="lp-exam-info-value lp-handwriting">Ayşe Kaya</span>
                      </div>
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.number')}</span>
                        <span className="lp-exam-info-value lp-handwriting">1047</span>
                      </div>
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.date')}</span>
                        <span className="lp-exam-info-value lp-handwriting">14.05.2025</span>
                      </div>
                    </div>
                    <div className="lp-exam-divider" />
                    <div className="lp-exam-q">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">1a.</span>
                        <span className="lp-exam-q-text">Hücre zarının seçici geçirgenliğini tanımlayınız. <span className="lp-exam-pts">(5p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area">
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">Hücre zarı bazı maddelerin geçişine izin verirken</span></div>
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">diğerlerini engeller, madde boyutu belirleyicidir.</span></div>
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">Kimyasal yapı da geçişi etkiler.</span></div>
                        <div className="lp-answer-line lp-line-blank" />
                        <div className="lp-exam-q-mark lp-mark-check">✓ <span>5/5</span></div>
                      </div>
                    </div>
                    <div className="lp-exam-q">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">1b.</span>
                        <span className="lp-exam-q-text">Pasif ve aktif taşıma farkını örnekle açıklayınız. <span className="lp-exam-pts">(5p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area">
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">Pasif taşıma enerji gerektirmez, difüzyon örnek</span></div>
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">verilebilir. Aktif taşıma ATP harcar.</span></div>
                        <div className="lp-answer-line lp-line-blank" />
                        <div className="lp-exam-q-mark lp-mark-partial">△ <span>3/5</span></div>
                      </div>
                    </div>
                    <div className="lp-exam-q lp-mobile-hide">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">2a.</span>
                        <span className="lp-exam-q-text">Fotosentez ışık ve karanlık evresini açıklayınız. <span className="lp-exam-pts">(10p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area">
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">Işık evresi tilakoid zarında gerçekleşir. Su ayrışır,</span></div>
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">ATP ve NADPH üretilir. Karanlık evre stromada</span></div>
                        <div className="lp-answer-line"><span className="lp-handwriting lp-ink-blue">Calvin döngüsü ile CO₂ şekere dönüştürülür.</span></div>
                        <div className="lp-exam-q-mark lp-mark-check">✓ <span>10/10</span></div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="math-sheet"
                    className="lp-exam-sheet"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="lp-exam-school-header">
                      <div className="lp-exam-school-name">{t('showcase.examInfo.schoolName')}</div>
                      <div className="lp-exam-title-row">
                        <span className="lp-exam-course">{t('showcase.examInfo.mathCourse')}</span>
                        <span className="lp-exam-term">{t('showcase.examInfo.term')}</span>
                      </div>
                      <div className="lp-paper-badge lp-paper-badge--inline">{t('showcase.uploaded')}</div>
                    </div>
                    <div className="lp-exam-info-grid">
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.name')}</span>
                        <span className="lp-exam-info-value lp-handwriting">Mehmet Demir</span>
                      </div>
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.number')}</span>
                        <span className="lp-exam-info-value lp-handwriting">1031</span>
                      </div>
                      <div className="lp-exam-info-cell">
                        <span className="lp-exam-info-label">{t('showcase.examInfo.date')}</span>
                        <span className="lp-exam-info-value lp-handwriting">14.05.2025</span>
                      </div>
                    </div>
                    <div className="lp-exam-divider" />

                    {/* Q1: Quadratic */}
                    <div className="lp-exam-q">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">1.</span>
                        <span className="lp-exam-q-text">x² − 5x + 6 = 0 denklemini çözünüz. <span className="lp-exam-pts">(10p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area">
                        <div className="lp-formula-block">
                          <span className="lp-handwriting lp-ink-blue lp-formula-line">Δ = b² − 4ac = 25 − 24 = 1</span>
                        </div>
                        <div className="lp-formula-block">
                          <span className="lp-handwriting lp-ink-blue lp-formula-line">x = (5 ± √1) / 2</span>
                        </div>
                        <div className="lp-answer-line">
                          <span className="lp-handwriting lp-ink-blue lp-answer-boxed">x₁ = 3 &nbsp;&nbsp; x₂ = 2</span>
                        </div>
                        <div className="lp-exam-q-mark lp-mark-check">✓ <span>10/10</span></div>
                      </div>
                    </div>

                    {/* Q2: Geometry with SVG figure */}
                    <div className="lp-exam-q">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">2.</span>
                        <span className="lp-exam-q-text">AB = 6, BC = 8, ∠B = 90° ise AC = ? <span className="lp-exam-pts">(10p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area lp-answer-with-figure">
                        {/* Handwritten SVG triangle */}
                        <svg className="lp-triangle-figure" viewBox="0 0 110 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                          {/* Triangle sides — slightly wobbly for hand-drawn feel */}
                          <path d="M 14 68 L 14 16 L 78 68 Z" stroke="#1a3a6e" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(26,58,110,0.04)" />
                          {/* Right angle marker */}
                          <path d="M 14 54 L 24 54 L 24 68" stroke="#1a3a6e" strokeWidth="1.1" fill="none" />
                          {/* Labels */}
                          <text x="4" y="44" fill="#1a3a6e" fontSize="9" fontFamily="Caveat, cursive">6</text>
                          <text x="40" y="78" fill="#1a3a6e" fontSize="9" fontFamily="Caveat, cursive">8</text>
                          <text x="50" y="36" fill="#1a3a6e" fontSize="9" fontFamily="Caveat, cursive" fontStyle="italic">?</text>
                          {/* Vertex labels */}
                          <text x="3"  y="14" fill="#142132" fontSize="8" fontFamily="Caveat, cursive">A</text>
                          <text x="2"  y="76" fill="#142132" fontSize="8" fontFamily="Caveat, cursive">B</text>
                          <text x="79" y="76" fill="#142132" fontSize="8" fontFamily="Caveat, cursive">C</text>
                        </svg>
                        <div className="lp-figure-workings">
                          <div className="lp-formula-block">
                            <span className="lp-handwriting lp-ink-blue lp-formula-line">AC² = AB² + BC²</span>
                          </div>
                          <div className="lp-formula-block">
                            <span className="lp-handwriting lp-ink-blue lp-formula-line">AC² = 36 + 64 = 100</span>
                          </div>
                          <div className="lp-answer-line">
                            <span className="lp-handwriting lp-ink-blue lp-answer-boxed">AC = 10</span>
                          </div>
                        </div>
                      </div>
                      <div className="lp-exam-q-mark lp-mark-check" style={{marginLeft: '0'}}>✓ <span>10/10</span></div>
                    </div>

                    {/* Q3: Derivative */}
                    <div className="lp-exam-q lp-mobile-hide">
                      <div className="lp-exam-q-prompt">
                        <span className="lp-exam-qnum">3.</span>
                        <span className="lp-exam-q-text">f(x) = 3x³ − 2x + 5 için f′(x) = ? <span className="lp-exam-pts">(5p)</span></span>
                      </div>
                      <div className="lp-exam-answer-area">
                        <div className="lp-answer-line">
                          <span className="lp-handwriting lp-ink-blue lp-answer-boxed">f′(x) = 9x² − 2</span>
                        </div>
                        <div className="lp-exam-q-mark lp-mark-partial">△ <span>3/5</span></div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeIn>

          {/* Arrow */}
          <FadeIn delay={0.25} className="lp-showcase-arrow">
            <div className="lp-arrow-wrap">
              {gradingState === 'grading' ? (
                <>
                  <Loader2 size={18} className="lp-spin" />
                  <span>{t('showcase.aiGradingWithStep', { current: gradingStep, total: SAMPLE_RESULT.questions.length })}</span>
                </>
              ) : gradingState === 'done' ? (
                <>
                  <CheckCircle size={18} />
                  <span>{t('showcase.aiGraded')}</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>{t('showcase.aiGrading')}</span>
                  <ArrowRight size={20} />
                </>
              )}
            </div>
          </FadeIn>

          {/* Right: report mockup — switches with subject */}
          <FadeIn delay={0.35} className="lp-showcase-col">
            <div className="lp-showcase-label">
              <span className="lp-showcase-dot lp-dot-report" />
              {t('showcase.reportLabel')}
            </div>
            <div className="lp-report-card">
              <AnimatePresence mode="wait">
                {gradingState === 'grading' ? (
                  <motion.div
                    key="grading-report"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                    className="lp-report-grading"
                  >
                    <div className="lp-report-grading-head">
                      <Loader2 size={20} className="lp-spin" />
                      <span>{t('showcase.aiGrading')}</span>
                    </div>
                    <div className="lp-report-grading-progress">
                      <div className="lp-report-grading-bar">
                        <motion.div
                          className="lp-report-grading-bar-fill"
                          animate={{ width: `${(gradingStep / SAMPLE_RESULT.questions.length) * 100}%` }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      </div>
                      <span className="lp-report-grading-step">{gradingStep}/{SAMPLE_RESULT.questions.length}</span>
                    </div>
                    <div className="lp-report-grading-hint">
                      {t('showcase.gradingHint')}
                    </div>
                  </motion.div>
                ) : gradingState === 'done' ? (
                  <motion.div
                    key="sample-report"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="lp-report-header">
                      <div>
                        <div className="lp-report-name">{SAMPLE_RESULT.studentNameLabel}</div>
                        <div className="lp-report-file">{SAMPLE_RESULT.fileName}</div>
                      </div>
                      <div className="lp-report-score-wrap">
                        <svg className="lp-score-ring" viewBox="0 0 44 44">
                          <circle cx="22" cy="22" r="17" fill="none" stroke="var(--lp-ring-track)" strokeWidth="3.5" />
                          {/* 88/100 → arc ≈ 94.0 of 106.8 total */}
                          <circle cx="22" cy="22" r="17" fill="none" stroke="#1E7A6E" strokeWidth="3.5"
                            strokeLinecap="round" strokeDasharray="94 12.8" strokeDashoffset="-24" />
                        </svg>
                        <div className="lp-report-score-label">
                          <span className="lp-report-score-val">{SAMPLE_RESULT.totalScore}</span>
                          <span className="lp-report-score-max">/ {SAMPLE_RESULT.maxPoints}</span>
                        </div>
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.overall')}</div>
                      <div className="lp-report-feedback">
                        {SAMPLE_RESULT.overallFeedback}
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.questionDetails')}</div>
                      <div className="lp-report-questions">
                        {SAMPLE_RESULT.questions.map((row, i) => (
                          <div key={row.q} className={`lp-report-qrow${i >= 5 ? ' lp-mobile-hide' : ''}`}>
                            <span className="lp-report-qnum">S{row.q}</span>
                            <div className="lp-report-qbody">
                              <span className="lp-report-qfeedback">{row.feedback}</span>
                              <span className="lp-report-qanswer">{t('showcase.answerPrefix', { answer: row.studentAnswer })}</span>
                            </div>
                            <span className={`lp-report-qscore ${row.score >= row.max ? 'lp-score-pass' : 'lp-score-partial'}`}>{row.score}/{row.max}</span>
                          </div>
                        ))}
                      </div>
                      <div className="lp-report-disclaimer">
                        {t('showcase.disclaimer')}
                      </div>
                    </div>
                  </motion.div>
                ) : showcaseSubject === 'bio' ? (
                  <motion.div
                    key="bio-report"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="lp-report-header">
                      <div>
                        <div className="lp-report-name">Ayşe Kaya</div>
                        <div className="lp-report-file">biyoloji_sinavi_s1.jpg</div>
                      </div>
                      <div className="lp-report-score-wrap">
                        <svg className="lp-score-ring" viewBox="0 0 44 44">
                          <circle cx="22" cy="22" r="17" fill="none" stroke="var(--lp-ring-track)" strokeWidth="3.5" />
                          <circle cx="22" cy="22" r="17" fill="none" stroke="#1E7A6E" strokeWidth="3.5"
                            strokeLinecap="round" strokeDasharray="94.8 12.8" strokeDashoffset="-24" />
                        </svg>
                        <div className="lp-report-score-label">
                          <span className="lp-report-score-val">87</span>
                          <span className="lp-report-score-max">/ 100</span>
                        </div>
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.overall')}</div>
                      <div className="lp-report-feedback">
                        Öğrenci temel kavramları iyi kavramış. S2a'da analiz becerisi güçlü. S1b'de örnekleme eksiklikleri var.
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.questionDetails')}</div>
                      <div className="lp-report-questions">
                        {[
                          { q: 'S1a', feedback: 'Tanım eksiksiz ve doğru.', score: 5, max: 5, pass: true },
                          { q: 'S1b', feedback: 'Kısmen doğru, örnekleme eksik.', score: 3, max: 5, pass: false },
                          { q: 'S2a', feedback: 'Mükemmel analiz, tam puan.', score: 10, max: 10, pass: true },
                          { q: 'S2b', feedback: 'Terminoloji hatası, yaklaşım doğru.', score: 7, max: 10, pass: false },
                        ].map((row, i) => (
                          <div key={row.q} className={`lp-report-qrow${i >= 2 ? ' lp-mobile-hide' : ''}`}>
                            <span className="lp-report-qnum">{row.q}</span>
                            <span className="lp-report-qfeedback">{row.feedback}</span>
                            <span className={`lp-report-qscore ${row.pass ? 'lp-score-pass' : 'lp-score-partial'}`}>{row.score}/{row.max}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="math-report"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="lp-report-header">
                      <div>
                        <div className="lp-report-name">Mehmet Demir</div>
                        <div className="lp-report-file">matematik_sinavi_m1.jpg</div>
                      </div>
                      <div className="lp-report-score-wrap">
                        <svg className="lp-score-ring" viewBox="0 0 44 44">
                          <circle cx="22" cy="22" r="17" fill="none" stroke="var(--lp-ring-track)" strokeWidth="3.5" />
                          {/* 82/100 → arc ≈ 87.4 of 106.8 total */}
                          <circle cx="22" cy="22" r="17" fill="none" stroke="#1E7A6E" strokeWidth="3.5"
                            strokeLinecap="round" strokeDasharray="87.4 19.4" strokeDashoffset="-24" />
                        </svg>
                        <div className="lp-report-score-label">
                          <span className="lp-report-score-val">82</span>
                          <span className="lp-report-score-max">/ 100</span>
                        </div>
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.overall')}</div>
                      <div className="lp-report-feedback">
                        Cebirsel işlemler güçlü, geometri ve ispat basamakları tam. Türev notasyonunda küçük hata var.
                      </div>
                    </div>
                    <div className="lp-report-section">
                      <div className="lp-report-section-title">{t('showcase.questionDetails')}</div>
                      <div className="lp-report-questions">
                        {[
                          { q: 'S1', feedback: 'Diskriminant ve kökler doğru.', score: 10, max: 10, pass: true },
                          { q: 'S2', feedback: 'Pisagor teoremi eksiksiz uygulandı.', score: 10, max: 10, pass: true },
                          { q: 'S3', feedback: 'Türev doğru, sabit terim atlandı.', score: 3, max: 5, pass: false },
                          { q: 'S4', feedback: 'Limit hesabı eksik bırakıldı.', score: 6, max: 10, pass: false },
                        ].map((row, i) => (
                          <div key={row.q} className={`lp-report-qrow${i >= 2 ? ' lp-mobile-hide' : ''}`}>
                            <span className="lp-report-qnum">{row.q}</span>
                            <span className="lp-report-qfeedback">{row.feedback}</span>
                            <span className={`lp-report-qscore ${row.pass ? 'lp-score-pass' : 'lp-score-partial'}`}>{row.score}/{row.max}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Before / After Transformation */}
      <section className="lp-transformation">
        <FadeIn className="lp-transformation-header">
          <span className="lp-transformation-eyebrow">{t('transformation.eyebrow')}</span>
          <h2>
            {t('transformation.titleLead')} <span className="lp-gradient-text">{t('transformation.titleEmph1')}</span>{t('transformation.titleMid')}{' '}
            <span className="lp-gradient-text">{t('transformation.titleEmph2')}</span>
          </h2>
        </FadeIn>

        <div className="lp-transformation-grid">
          {/* Mobile-only combined image */}
          <img
            src="/pre-post.png"
            alt={t('transformation.combinedAlt')}
            className="lp-transformation-combined-img"
          />

          {/* BEFORE card */}
          <FadeIn delay={0.1} className="lp-transformation-card lp-transformation-card--before">
            <div className="lp-transformation-img-wrap">
              <img
                src="/teacher-overwhelmed.png"
                alt={t('transformation.before.imgAlt')}
                className="lp-transformation-img"
              />
              <div className="lp-transformation-badge lp-transformation-badge--before">
                <X size={13} />
                {t('transformation.before.badge')}
              </div>
            </div>
            <div className="lp-transformation-body">
              <h3>{t('transformation.before.title')}</h3>
              <ul className="lp-transformation-list">
                {(t('transformation.before.items', { returnObjects: true }) as string[]).map((item) => (
                  <li key={item} className="lp-transformation-list-item">
                    <X size={14} className="lp-transformation-icon-bad" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          {/* Orange arrow divider */}
          <div className="lp-transformation-divider" aria-hidden>
            <div className="lp-transformation-arrow-wrap">
              <ArrowRight size={22} />
            </div>
          </div>

          {/* AFTER card */}
          <FadeIn delay={0.2} className="lp-transformation-card">
            <div className="lp-transformation-img-wrap">
              <img
                src="/teacher-happy.png"
                alt={t('transformation.after.imgAlt')}
                className="lp-transformation-img"
              />
              <div className="lp-transformation-corner-tag">{t('transformation.after.tag')}</div>
            </div>
            <div className="lp-transformation-body">
              <h3>{t('transformation.after.title')}</h3>
              <ul className="lp-transformation-list">
                {(t('transformation.after.items', { returnObjects: true }) as string[]).map((text, i) => (
                  <li key={text} className="lp-transformation-list-item lp-transformation-list-item--good">
                    <CheckCircle size={14} className="lp-transformation-icon-good" />
                    <span>{text}</span>
                    {i === 0 && (
                      <span className="lp-transformation-item-badge">{t('transformation.after.badgeFaster')}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>

        {/* Stat badges — attached to transformation section */}
        <FadeIn delay={0.3} className="lp-transformation-stats">
          <div className="lp-transformation-stat-badge">
            <span className="lp-transformation-stat-num">50K+</span>
            <span className="lp-transformation-stat-lbl">{t('transformation.stats.examsLabel')}</span>
          </div>
          <div className="lp-transformation-stat-divider" />
          <div className="lp-transformation-stat-badge">
            <span className="lp-transformation-stat-num">2.400+</span>
            <span className="lp-transformation-stat-lbl">{t('transformation.stats.educatorsLabel')}</span>
          </div>
        </FadeIn>
      </section>

      {/* Mobile app */}
      <MobileAppSection />

      {/* Pricing */}
      <section className="lp-pricing" id="pricing">
        <FadeIn className="lp-pricing-header">
          <h2>
            {t('pricing.titleLead')} <span className="lp-gradient-text">{t('pricing.titleEmph')}</span>
          </h2>
          <p>
            {t('pricing.subtitle')}
          </p>
        </FadeIn>

        <FadeIn y={30}>
          <div className="lp-glass-card lp-calc-wrapper">
            {/* LEFT: Inputs */}
            <div className="lp-calc-left">
              <p className="lp-calc-section-label">{t('pricing.examInfo')}</p>

              {!isMobile && (
                <div className="lp-calc-field lp-calc-field--inline">
                  <label className="lp-calc-field-label">{t('pricing.examType')}</label>
                  <div className="lp-calc-type-selector">
                    <button
                      className={examType === 'plain' ? 'active' : ''}
                      onClick={() => setExamType('plain')}
                    >
                      <FileText size={15} />
                      {t('pricing.plainText')}
                    </button>
                    <button
                      className={examType === 'formula' ? 'active' : ''}
                      onClick={() => setExamType('formula')}
                    >
                      <Calculator size={15} />
                      {t('pricing.formulaShape')}
                    </button>
                  </div>
                  {examType === 'formula' && (
                    <p className="lp-calc-field-hint">{t('pricing.formulaHint')}</p>
                  )}
                </div>
              )}

              {isMobile ? (
                <div className="lp-calc-field lp-calc-field-compact lp-calc-field--inline">
                  <label className="lp-calc-field-label">{t('pricing.annualPapers')}</label>
                  <div className="lp-calc-stepper">
                    <button
                      className="lp-calc-step-btn"
                      onClick={() => setAnnualPapers(n => Math.max(10, n - 10))}
                      disabled={annualPapers <= 10}
                    >&minus;</button>
                    <EditableStepValue
                      value={annualPapers}
                      min={10}
                      max={10000}
                      onCommit={setAnnualPapers}
                    />
                    <button
                      className="lp-calc-step-btn"
                      onClick={() => setAnnualPapers(n => Math.min(10000, n + 10))}
                    >+</button>
                  </div>
                </div>
              ) : (
                <div className="lp-calc-inputs-row">
                  <div className="lp-calc-field lp-calc-field-compact">
                    <label className="lp-calc-field-label">{t('pricing.totalStudents')}</label>
                    <div className="lp-calc-stepper">
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setStudentsPerClass(s => Math.max(10, s - 10))}
                        disabled={studentsPerClass <= 10}
                      >&minus;</button>
                      <EditableStepValue
                        value={studentsPerClass}
                        min={10}
                        max={500}
                        onCommit={setStudentsPerClass}
                      />
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setStudentsPerClass(s => Math.min(500, s + 10))}
                      >+</button>
                    </div>
                  </div>
                  <div className="lp-calc-field lp-calc-field-compact">
                    <label className="lp-calc-field-label">{t('pricing.pagesPerStudent')}</label>
                    <div className="lp-calc-stepper">
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setPagesPerStudent(p => Math.max(1, p - 1))}
                        disabled={pagesPerStudent <= 1}
                      >&minus;</button>
                      <span className="lp-calc-step-val">{pagesPerStudent}</span>
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setPagesPerStudent(p => Math.min(20, p + 1))}
                        disabled={pagesPerStudent >= 20}
                      >+</button>
                    </div>
                  </div>
                  <div className="lp-calc-field lp-calc-field-compact">
                    <label className="lp-calc-field-label">{t('pricing.examsPerYear')}</label>
                    <div className="lp-calc-stepper">
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setExamsPerYear(e => Math.max(1, e - 1))}
                        disabled={examsPerYear <= 1}
                      >&minus;</button>
                      <span className="lp-calc-step-val">{examsPerYear}</span>
                      <button
                        className="lp-calc-step-btn"
                        onClick={() => setExamsPerYear(e => Math.min(20, e + 1))}
                      >+</button>
                    </div>
                  </div>
                </div>
              )}

              {!isMobile && pagesPerStudent > 3 && (
                <p className="lp-calc-field-hint">{t('pricing.pagesHint')}</p>
              )}
            </div>

            <div className="lp-calc-divider" />

            {/* RIGHT: Result */}
            <div className="lp-calc-right">
              <p className="lp-calc-section-label">{t('pricing.estimatedCost')}</p>

              <div className="lp-calc-price-box">
                <div className="lp-calc-price-top">
                  <AnimatedPrice value={displayTotal} />
                  <AnimatePresence>
                    {annualPricing.discountPct > 0 && (
                      <motion.span
                        className="lp-calc-discount-badge"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                      >
                        {t('pricing.discount', { pct: annualPricing.discountPct % 1 === 0 ? annualPricing.discountPct.toFixed(0) : annualPricing.discountPct.toFixed(1) })}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <div className="lp-calc-per-credit">
                  <span className="lp-gradient-text">{displayPerReading}</span>
                  {' '}{t('pricing.perReading')}
                  {annualPricing.discountPct === 0 && (
                    <span className="lp-calc-per-credit-hint">{t('pricing.perReadingHint')}</span>
                  )}
                </div>
                {!isMobile && (
                  <div className="lp-calc-breakdown">
                    <div className="lp-calc-breakdown-row">
                      <span>{t('pricing.annualTotal', { count: examsPerYear })}</span>
                      <span className="lp-calc-breakdown-val">{t('pricing.readings', { value: displayAnnualReadings.toLocaleString(readingsLocale) })}</span>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="lp-btn-primary lp-btn-pricing"
                onClick={() => navigate('/exams')}
              >
                {t('pricing.getStarted')} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* FAQ */}
      <section className="lp-faq" id="faq">
        <FadeIn className="lp-faq-header">
          <h2>
            {t('faq.titleLead')} <span className="lp-gradient-text">{t('faq.titleEmph')}</span>
          </h2>
          <p>{t('faq.subtitle')}</p>
        </FadeIn>

        <div className="lp-faq-list">
          {compactFeatures.map((feature, index) => (
            <FaqItem key={index} feature={feature} index={index} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="lp-cta">
        <FadeIn>
          <h2>{t('cta.title')}</h2>
          <p>
            {t('cta.subtitle')}
          </p>

          <div className="lp-cta-social">
            <div className="lp-cta-avatars">
              <div className="lp-cta-avatar">E</div>
              <div className="lp-cta-avatar">A</div>
              <div className="lp-cta-avatar">M</div>
              <div className="lp-cta-avatar">S</div>
            </div>
            <span><Trans i18nKey="cta.social" t={t} components={{ 1: <strong /> }} /></span>
          </div>

          <button
            className="lp-btn-primary lp-btn-lg lp-btn-cta-glow"
            onClick={() => navigate('/exams')}
          >
            {t('cta.button')} <ArrowRight size={18} />
          </button>
          <p className="lp-cta-credits">{t('cta.credits')}</p>
        </FadeIn>
      </section>

      <footer className="lp-footer">
        <p>{t('footer.copyright')}</p>
      </footer>
    </div>
  )
}
